import { memo, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, List, PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import { Card, Button, Input } from '../ui';
import { cn } from '../../lib/cn';
import PdfDisplay from '../stimulus/PdfDisplay';
import ChapterReader from './ChapterReader';
import AnnotationSidebar from './AnnotationSidebar';

/**
 * The full book reading surface: search + table-of-contents (left), the chapter
 * reader (center), and the annotation sidebar (right). Extracted from
 * Teacher/Library/Show so the exact same experience can be mounted inside a
 * Live Session (the teacher's driving seat) — there is no "live-session version"
 * of the reader.
 *
 * Live-session integration (all optional, no-ops by default):
 *   - readerScrollRef: external ref for the chapter scroll container, so the
 *     live page can attach the paragraph-anchor broadcaster to the same element.
 *   - onChapterLoaded({ chapter_id, chapter, html, analyzed_sentences, selections }):
 *     fired when a chapter finishes loading — the teacher broadcasts it as
 *     `set_chapter` so the student mirrors the same chapter.
 *   - onSelectionsChanged(chapterId, selections): fired whenever the active
 *     chapter's selections change — broadcast as `reader_selections`.
 */
function BookReader({
  book,
  chapters,
  courseListOptions = null,
  initialChapterId = null,
  pinnedParagraphIndex = null,
  readerScrollRef: externalScrollRef = null,
  onChapterLoaded = null,
  onSelectionsChanged = null,
}) {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [focusedChunk, setFocusedChunk] = useState(null); // search-result-derived highlight target
  const [activeChapter, setActiveChapter] = useState(null);
  const [chapterFullHtml, setChapterFullHtml] = useState('');
  const [chapterSelections, setChapterSelections] = useState([]);
  const [chapterAnalyzedSentences, setChapterAnalyzedSentences] = useState([]);
  const [activeChapterMeta, setActiveChapterMeta] = useState(null);
  const [tocCollapsed, setTocCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('studyfit-toc-collapsed') === 'true';
  });
  const [chapterPopoverOpen, setChapterPopoverOpen] = useState(false);
  const [focusParagraphIndex, setFocusParagraphIndex] = useState(null);
  const internalScrollRef = useRef(null);
  const readerScrollRef = externalScrollRef || internalScrollRef;

  const hasMultipleChapters = chapters.length > 1;

  useEffect(() => {
    localStorage.setItem('studyfit-toc-collapsed', String(tocCollapsed));
  }, [tocCollapsed]);

  // Deep-link to a chapter (Library passes ?chapter=ID); otherwise auto-select
  // the only chapter for single-chapter books.
  useEffect(() => {
    if (initialChapterId) {
      selectChapter(Number(initialChapterId));
    } else if (!hasMultipleChapters && chapters.length === 1) {
      selectChapter(chapters[0].id);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch the most relevant paragraph when viewing a search result.
  useEffect(() => {
    if (focusedChunk && activeQuery) {
      setFocusParagraphIndex(null);
      fetch(`/teacher/library/chunks/${focusedChunk.id}/focus`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({ query: activeQuery }),
      })
        .then(res => res.json())
        .then(data => setFocusParagraphIndex(data.paragraph_index))
        .catch(() => setFocusParagraphIndex(null));
    } else {
      setFocusParagraphIndex(null);
    }
  }, [focusedChunk?.id, activeQuery]);

  const focusedChunkRange = useMemo(() => {
    if (!focusedChunk) return null;
    return { start: focusedChunk.paragraph_start, end: focusedChunk.paragraph_end };
  }, [focusedChunk]);

  const focusParagraphChapterIndex = useMemo(() => {
    if (!focusedChunk || focusParagraphIndex == null) return null;
    return focusedChunk.paragraph_start + focusParagraphIndex;
  }, [focusedChunk, focusParagraphIndex]);

  const fetchChapterHtml = useCallback(async (chapterId) => {
    try {
      const response = await fetch(`/teacher/library/${book.id}/chapters/${chapterId}/html`);
      return await response.json();
    } catch (error) {
      console.error('Failed to fetch chapter HTML:', error);
      return { html: '', chapter: null, book: null };
    }
  }, [book.id]);

  const loadChapterContent = useCallback(async (chapterId) => {
    setActiveChapter(chapterId);
    setChapterSelections([]);
    setChapterAnalyzedSentences([]);

    const data = await fetchChapterHtml(chapterId);
    setActiveChapterMeta(data.chapter);
    setChapterFullHtml(data.html || '');
    setChapterAnalyzedSentences(data.analyzed_sentences || []);

    let selections = [];
    try {
      const res = await fetch(`/teacher/library/${book.id}/chapters/${chapterId}/text_selections`);
      if (res.ok) {
        const payload = await res.json();
        selections = payload.selections || [];
      }
    } catch {
      // Non-fatal; reader just loads without existing selections.
    }
    setChapterSelections(selections);

    // Enrich the fetched chapter ({ id, title }) with the nicer formatted_label
    // from the TOC list so followers can show "Ch. 1: Loomings".
    const meta = chapters.find((c) => c.id === chapterId);
    const chapter = { ...(data.chapter || { id: chapterId }), formatted_label: meta?.formatted_label };

    onChapterLoaded?.({
      chapter_id: chapterId,
      chapter,
      book: { title: book.title, author: book.author },
      html: data.html || '',
      analyzed_sentences: data.analyzed_sentences || [],
      selections,
    });
  }, [fetchChapterHtml, book.id, book.title, book.author, chapters, onChapterLoaded]);

  // TOC click — clears search and focus state, then loads the chapter.
  const selectChapter = useCallback(async (chapterId) => {
    setFocusedChunk(null);
    setSearchResults(null);
    setSearchInput('');
    setActiveQuery('');
    await loadChapterContent(chapterId);
  }, [loadChapterContent]);

  const executeSearch = useCallback(async () => {
    if (searchInput.length < 3) {
      setSearchResults(null);
      return;
    }

    setActiveQuery(searchInput);
    setSearching(true);
    try {
      const response = await fetch('/teacher/library/search', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({ query: searchInput, book_id: book.id }),
      });
      const data = await response.json();
      setSearchResults(data.results);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  }, [searchInput, book.id]);

  const deleteSelection = useCallback(async (selection) => {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
    const res = await fetch(
      `/teacher/library/${book.id}/chapters/${activeChapter}/text_selections/${selection.id}`,
      { method: 'DELETE', headers: { 'X-CSRF-Token': csrf } }
    );
    if (res.ok) {
      setChapterSelections((prev) => prev.filter((s) => s.id !== selection.id));
    }
  }, [book.id, activeChapter]);

  // Broadcast selection changes to the live page (teacher → student). Fires on
  // load (initial set), highlight add (ChapterReader), and delete (sidebar).
  useEffect(() => {
    if (!onSelectionsChanged || !activeChapter) return;
    onSelectionsChanged(activeChapter, chapterSelections);
  }, [chapterSelections, activeChapter, onSelectionsChanged]);

  const scrollToSelection = useCallback((selection) => {
    const container = readerScrollRef.current;
    if (!container) return;
    const mark = container.querySelector(`mark[data-selection-id="${selection.id}"]`);
    if (mark) {
      mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
      mark.classList.add('ring-2', 'ring-blue-400');
      setTimeout(() => mark.classList.remove('ring-2', 'ring-blue-400'), 1500);
    }
  }, [readerScrollRef]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      executeSearch();
    }
  }, [executeSearch]);

  const handleSelectSearchResult = useCallback(async (result) => {
    setFocusedChunk(result);
    if (activeChapter !== result.chapter.id) {
      setChapterFullHtml(''); // explicit clear avoids briefly highlighting the wrong chapter
      await loadChapterContent(result.chapter.id);
    }
  }, [activeChapter, loadChapterContent]);

  const clearSearch = useCallback(() => {
    setSearchInput('');
    setSearchResults(null);
    setActiveQuery('');
    setFocusedChunk(null);
    setFocusParagraphIndex(null);
  }, []);

  // Scroll the focused paragraph into the center of the chapter scroll container.
  useEffect(() => {
    if (!chapterFullHtml) return;
    if (focusParagraphChapterIndex == null) return;
    if (focusedChunk?.chapter?.id !== activeChapter) return;
    const container = readerScrollRef.current;
    if (!container) return;
    requestAnimationFrame(() => {
      const para = container.querySelector(`[data-paragraph-index="${focusParagraphChapterIndex}"]`);
      if (para) para.scrollIntoView({ behavior: 'auto', block: 'center' });
    });
  }, [chapterFullHtml, focusParagraphChapterIndex, focusedChunk?.chapter?.id, activeChapter, readerScrollRef]);

  return (
    <div className="flex gap-0">
      {/* ── Left Panel: Search + TOC (collapsible) ── */}
      <div
        className={cn(
          'flex-shrink-0 transition-[width] duration-200 ease-in-out overflow-hidden hidden lg:block',
          tocCollapsed ? 'w-14' : (searchResults ? 'w-96' : 'w-64')
        )}
      >
        {tocCollapsed ? (
          <CollapsedTocStrip
            chapters={chapters}
            activeChapter={activeChapter}
            hasMultipleChapters={hasMultipleChapters}
            onExpand={() => setTocCollapsed(false)}
            onSelectChapter={selectChapter}
            popoverOpen={chapterPopoverOpen}
            setPopoverOpen={setChapterPopoverOpen}
          />
        ) : (
          <div className="space-y-4 pr-4">
            <button
              onClick={() => setTocCollapsed(true)}
              className="flex items-center gap-2 text-xs text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              title={t('common.collapsePanel')}
            >
              <PanelLeftClose className="w-4 h-4" strokeWidth={1.5} />
              <span>{t('nav.collapse')}</span>
            </button>

            {/* Search */}
            <Card className="p-4">
              <div className="relative flex gap-2">
                <div className="relative flex-1">
                  <Input
                    type="text"
                    placeholder={t('teacherLibrary.searchPassages')}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="w-full pr-8"
                  />
                  {(searchInput || searchResults) && (
                    <button
                      type="button"
                      onClick={clearSearch}
                      title="Clear search"
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                  )}
                </div>
                <Button
                  variant="secondary"
                  onClick={executeSearch}
                  disabled={searching || searchInput.length < 3}
                  className="shrink-0"
                >
                  {searching ? <Spinner className="w-4 h-4" /> : <SearchIcon className="w-4 h-4" />}
                </Button>
              </div>

              {searchResults && (
                <div className="mt-4 space-y-3 max-h-96 overflow-y-auto">
                  {searchResults.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400">{t('common.noResults')}</p>
                  ) : (
                    searchResults.map((result) => (
                      <button
                        key={result.id}
                        onClick={() => handleSelectSearchResult(result)}
                        className={cn(
                          'w-full text-left p-3 rounded-lg border transition-colors',
                          focusedChunk?.id === result.id
                            ? 'border-gray-400 bg-gray-50 dark:bg-gray-700 dark:border-gray-500'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                        )}
                      >
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{result.chapter.title}</p>
                        <p className="text-sm text-gray-900 dark:text-white line-clamp-3">{result.body}</p>
                      </button>
                    ))
                  )}
                </div>
              )}
            </Card>

            {/* TOC */}
            {hasMultipleChapters && (
              <Card className="p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
                  {t('teacherLibrary.tableOfContents')} ({chapters.length})
                </h3>
                <div className="space-y-1 max-h-[400px] overflow-y-auto">
                  {chapters.map((chapter) => (
                    <button
                      key={chapter.id}
                      onClick={() => selectChapter(chapter.id)}
                      className={cn(
                        'w-full text-left px-3 py-2 rounded-lg text-sm transition-colors',
                        activeChapter === chapter.id
                          ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800',
                        (chapter.chapter_type === 'front_matter' || chapter.chapter_type === 'back_matter') && 'italic'
                      )}
                    >
                      <span className="flex items-baseline justify-between gap-2">
                        <span className="line-clamp-1">{chapter.formatted_label || chapter.title}</span>
                        {chapter.word_count > 0 && (
                          <span className="shrink-0 text-xs text-gray-400 dark:text-gray-500 font-normal not-italic">
                            {Math.max(1, Math.ceil(chapter.word_count / 300))} p
                          </span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* ── Center Panel: reader ── */}
      <div className="flex-1 min-w-0">
        {book.item_type === 'pdf' && book.file_url ? (
          <PdfDisplay fileUrl={book.file_url} />
        ) : (
          <Card className="p-8 min-h-[600px]">
            {chapterFullHtml ? (
              <div className="max-w-2xl mx-auto">
                {hasMultipleChapters && (
                  <div className="mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
                    <p className="text-sm text-gray-500 dark:text-gray-400">{book.title}</p>
                    <p className="text-lg font-medium text-gray-900 dark:text-white">
                      {activeChapterMeta?.title}
                    </p>
                  </div>
                )}
                <div ref={readerScrollRef} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 max-h-[calc(100vh-12rem)] overflow-y-auto">
                  <div className="px-10 py-8">
                    <ChapterReader
                      book={book}
                      chapter={activeChapterMeta}
                      html={chapterFullHtml}
                      initialSelections={chapterSelections}
                      analyzedSentences={chapterAnalyzedSentences}
                      onSelectionsChange={setChapterSelections}
                      focusedChunkRange={focusedChunkRange}
                      focusedParagraphIndex={focusParagraphChapterIndex ?? pinnedParagraphIndex}
                      courseListOptions={courseListOptions}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500">
                <div className="text-center">
                  <BookIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{hasMultipleChapters ? t('teacherLibrary.searchForPassages') : t('teacherLibrary.searchToStart')}</p>
                </div>
              </div>
            )}
          </Card>
        )}
      </div>

      {/* ── Right Panel: annotation sidebar ── */}
      {chapterFullHtml && (
        <div className="hidden lg:block w-72 flex-shrink-0 pl-4">
          <div className="sticky top-0 max-h-[calc(100vh-12rem)] overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-800 text-[10px] font-semibold uppercase tracking-[0.12em] text-gray-400 dark:text-gray-500">
              Annotations
            </div>
            <AnnotationSidebar
              selections={chapterSelections}
              onScrollTo={scrollToSelection}
              onDelete={deleteSelection}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Collapsed TOC strip ─────────────────────────────────────────
function CollapsedTocStrip({
  chapters,
  activeChapter,
  hasMultipleChapters,
  onExpand,
  onSelectChapter,
  popoverOpen,
  setPopoverOpen,
}) {
  const popoverRef = useRef(null);

  useEffect(() => {
    if (!popoverOpen) return;
    const handler = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setPopoverOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popoverOpen, setPopoverOpen]);

  return (
    <div className="flex flex-col items-center gap-3 pt-2">
      <button
        onClick={onExpand}
        className="p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        title="Expand panel"
      >
        <PanelLeftOpen className="w-4 h-4" strokeWidth={1.5} />
      </button>

      {false && (
        <>
          <button
            onClick={onExpand}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            title="Search passages"
          >
            <Search className="w-4 h-4" strokeWidth={1.5} />
          </button>

          {hasMultipleChapters && (
            <div className="relative" ref={popoverRef}>
              <button
                onClick={() => setPopoverOpen(!popoverOpen)}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                title="Chapters"
              >
                <List className="w-4 h-4" strokeWidth={1.5} />
              </button>

              {popoverOpen && (
                <div className="absolute left-12 top-0 z-40 w-64 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl p-2 max-h-80 overflow-y-auto">
                  <div className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide px-2 py-1 mb-1">
                    Chapters
                  </div>
                  {chapters.map((chapter) => (
                    <button
                      key={chapter.id}
                      onClick={() => {
                        onSelectChapter(chapter.id);
                        setPopoverOpen(false);
                      }}
                      className={cn(
                        'w-full text-left px-2 py-1.5 rounded text-sm transition-colors',
                        activeChapter === chapter.id
                          ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800',
                        (chapter.chapter_type === 'front_matter' || chapter.chapter_type === 'back_matter') && 'italic'
                      )}
                    >
                      <span className="line-clamp-1">{chapter.formatted_label || chapter.title}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Spinner({ className }) {
  return (
    <svg className={cn('animate-spin', className)} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
}

function BookIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  );
}

function SearchIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

// Memoized so the live-session page's high-frequency re-renders (cursor moves,
// presence) don't re-render the reader subtree — re-rendering mid-drag was
// disturbing the teacher's in-progress text selection. The props from both
// hosts (Library/Show, LiveSessions/Show) are stable references.
export default memo(BookReader);
